import { Banner, Button, Dialog, LoadingSpinner, TextInput, Tooltip, Typography } from '@neo4j-ndl/react';
import { AURA_CONSOLE_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import type { ChurnSurveyData } from '@nx/plg';
import { ChurnSurvey, getChurnSurveyDefaults, transformChurnSurveyData, validateChurnSurveyData } from '@nx/plg';
import type { Instance, Project } from '@nx/state';
import { consoleApi, getApiError, getErrorMessage, useActiveOrg } from '@nx/state';
import type { Validation } from '@nx/stdlib';
import { isNotNullish } from '@nx/stdlib';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SyntheticEvent } from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { constructCustomEndpointUrl } from '../../custom-endpoints/helpers';
import { useTrackUpxEvent } from '../../services/segment/analytics';

const logger = createLogger(APP_SCOPE.framework);

interface Props {
  onClose: () => void;
  onConfirm: () => void;
  instance: Instance;
  project: Project;
}
export const DeleteInstanceModal = ({ onClose, onConfirm, instance, project }: Props) => {
  const trackEvent = useTrackUpxEvent();
  const [confirmText, setConfirmText] = useState('');
  const [tipOpen, setTipOpen] = useState(false);
  const [destroy, destroyRes] = consoleApi.useDestroyInstanceMutation();
  const { name } = instance;
  const handleConfirmTextChange = (value: string) => {
    setConfirmText(value);
  };
  const organization = useActiveOrg();
  const churnSurveyShouldBeSubmittedResponse = consoleApi.useChurnSurveyShouldBeSubmittedQuery(
    {
      organizationId: organization.id,
      dbId: instance.id,
    },
    { refetchOnMountOrArgChange: true },
  );
  const churnSurveyShouldBeSubmitted = churnSurveyShouldBeSubmittedResponse.data === true;
  const [churnSurveyData, setChurnSurveyData] = useState(getChurnSurveyDefaults());
  const [churnSurveyValidation, setChurnSurveyValidation] = useState<Validation<ChurnSurveyData> | null>(null);
  const [addChurnSurvey, addChurnSurveyRes] = consoleApi.useAddChurnSurveyMutation();

  // Used to avoid gap between addChurnSurvey.isLoading and destroy.isLoading
  const [isLoading, setIsLoading] = useState(false);

  const { data: customEndpoints = [] } = consoleApi.useListCustomEndpointByInstanceQuery(instance.id);

  const errorMessage = useMemo(() => {
    let responseError = undefined;

    if (destroyRes.isError) {
      responseError = destroyRes.error;
    } else if (addChurnSurveyRes.isError) {
      responseError = addChurnSurveyRes.error;
    } else {
      return null;
    }

    const error = getApiError(responseError);
    const message = getErrorMessage(error);
    return message;
  }, [destroyRes.error, destroyRes.isError, addChurnSurveyRes.error, addChurnSurveyRes.isError]);

  const handleClose = () => {
    onClose();
    setConfirmText('');
    setChurnSurveyData(getChurnSurveyDefaults());
    setChurnSurveyValidation(null);
  };

  const submitChurnSurvey = () => {
    const toSubmit = transformChurnSurveyData(churnSurveyData);

    return addChurnSurvey({
      ...toSubmit,
      organizationId: organization.id,
      dbid: instance.id,
    });
  };

  const conditionallySubmitChurnSurvey = () => {
    if (churnSurveyShouldBeSubmitted) {
      return submitChurnSurvey().unwrap();
    }
    return Promise.resolve();
  };

  const handleDelete = (event: SyntheticEvent) => {
    event.preventDefault();

    setIsLoading(true);
    // Send requests sequentially to avoid confusion if one fails
    conditionallySubmitChurnSurvey()
      .then(() => destroy(instance.id).unwrap())
      .then(() => {
        onConfirm();
      })
      .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
        if (isNotNullish(e)) {
          logger.error(e);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    trackEvent({
      event: AURA_CONSOLE_EVENTS.INSTANCE_DESTROY,
      properties: { tier: instance.tier },
      scope: APP_SCOPE.aura,
    });
  };

  useEffect(() => {
    if (tipOpen) {
      setTimeout(() => {
        setTipOpen(false);
      }, 2000);
    }
  }, [tipOpen]);

  const handleChurnSurveyChange = (data: ChurnSurveyData) => {
    setChurnSurveyData(data);
    const validation = validateChurnSurveyData(data);
    setChurnSurveyValidation(validation);
  };

  return (
    <Dialog isOpen onClose={handleClose} size="small">
      <Dialog.Header className="flex flex-col gap-1">
        <div>
          Delete {churnSurveyShouldBeSubmitted && 'your last deployment '}
          {/* TODO: @team-console should placement be left or right */}
          <Tooltip isOpen={tipOpen} type="simple" placement="right" isPortaled={false}>
            <Tooltip.Trigger
              className="cursor-text"
              htmlAttributes={{
                onClick: () => {
                  void navigator.clipboard.writeText(instance.name);
                  if (!tipOpen) {
                    setTipOpen(true);
                  }
                },
                'aria-label': 'Copy text',
              }}
            >
              <span className="font-normal">{instance.name}</span>
            </Tooltip.Trigger>
            <Tooltip.Content className="flex items-center">Copied to clipboard</Tooltip.Content>
          </Tooltip>{' '}
        </div>
        <Dialog.Description>
          This action is irreversible. We will delete your instance and all associated snapshots.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Content>
        {churnSurveyShouldBeSubmittedResponse.isFetching ? (
          <div className="flex justify-center">
            <LoadingSpinner size="large" />
          </div>
        ) : (
          <form onSubmit={handleDelete} id="delete-instance-form" className="flex flex-col gap-6">
            <TextInput
              value={confirmText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleConfirmTextChange(e.target.value)}
              label={
                <Typography variant="subheading-medium" className="text-neutral-text-default">
                  Type the name of the instance below to confirm
                </Typography>
              }
              htmlAttributes={{
                autoFocus: true,
                placeholder: instance.name,
              }}
              isFluid
            />
            {customEndpoints.length !== 0 && (
              <div>
                <Banner
                  type="warning"
                  description={
                    <span>
                      This instance is assigned to the following custom endpoints:
                      {customEndpoints.map((endpoint, i) => (
                        <Fragment key={endpoint.id}>
                          {i === 0 && <br />}
                          <i>{constructCustomEndpointUrl(endpoint)}</i>
                          <br />
                        </Fragment>
                      ))}
                      By deleting this instance, the custom endpoints will become disconnected until another instance is
                      assigned.
                    </span>
                  }
                />
              </div>
            )}
            {churnSurveyShouldBeSubmitted && (
              <ChurnSurvey
                onChange={handleChurnSurveyChange}
                data={churnSurveyData}
                validation={churnSurveyValidation}
              />
            )}
            {isNotNullish(errorMessage) && <Banner type="danger" description={errorMessage} usage="inline" />}
          </form>
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <Button color="neutral" fill="outlined" onClick={handleClose} isDisabled={isLoading}>
          Cancel
        </Button>
        <Button
          color="danger"
          type="submit"
          isDisabled={
            confirmText !== name ||
            Boolean(
              churnSurveyShouldBeSubmitted &&
                (churnSurveyValidation ?? Object.values(churnSurveyData.reasons).every((v) => !v)),
            )
          }
          isLoading={isLoading}
          htmlAttributes={{
            form: 'delete-instance-form',
          }}
        >
          Delete
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
